import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { EmptyState } from "@/components/common/EmptyState";
import { DataScope, monthOf } from "@/components/common/DataScope";
import {
    ResponsiveContainer,
    LineChart,
    Line,
    ComposedChart,
    Bar,
    PieChart,
    Pie,
    Cell,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
} from "recharts";
import {
    Compass,
    ChevronDown,
    CalendarDays,
    CalendarRange,
    Package,
    Store,
    PieChart as PieIcon,
    ArrowUpRight,
    ArrowDownRight,
} from "lucide-react";
import { OPS } from "@/constants/testIds";
import {
    fmtByUnit,
    fmtNumber,
    fmtCompact,
    currencySuffix,
    shortDate,
    localeDate,
    CHART_COLORS,
} from "@/lib/format";

const AXIS_TICK = { fontSize: 11, fill: "var(--ink-muted)" };
const GRID_COLOR = "var(--border-soft)";
// Same 3-channel palette used by the classic Channel-Mix / Daily-by-Channel panels.
const CH_COLORS = { walk_in: "#D1802F", call_center: "#8F340C", delivery: "#BAD9EB" };
const CHANNEL_KEYS = ["walk_in", "call_center", "delivery"];

const GROUP_OPTIONS = [
    { key: "day", labelKey: "exp_gb_day", icon: CalendarDays },
    { key: "month", labelKey: "exp_gb_month", icon: CalendarRange },
    { key: "item", labelKey: "exp_gb_item", icon: Package },
    { key: "branch", labelKey: "exp_gb_branch", icon: Store },
    { key: "channel", labelKey: "exp_gb_channel", icon: PieIcon },
];
const GROUP_ICON = Object.fromEntries(GROUP_OPTIONS.map((o) => [o.key, o.icon]));

// Summary strip cards, in display order. Rendered only when a value exists.
const SUMMARY_DEFS = [
    { key: "net", labelKey: "exp_sum_net", unit: "currency" },
    { key: "invoices", labelKey: "exp_sum_invoices", unit: "number" },
    { key: "aov", labelKey: "exp_sum_aov", unit: "currency" },
    { key: "cash", labelKey: "exp_sum_cash", unit: "currency" },
    { key: "card", labelKey: "exp_sum_card", unit: "currency" },
    { key: "delivery", labelKey: "exp_sum_delivery", unit: "currency" },
];

// ---------------------------------------------------------------- data hook
function useExplore(groupBy, branch, start, end, refreshKey, compare) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        let active = true;
        setLoading(true);
        const params = { group_by: groupBy, branch: branch || "all" };
        if (start) params.start = start;
        if (end) params.end = end;
        if (compare) params.compare = 1;
        api
            .get("/sales/explore", { params })
            .then((r) => active && setData(r.data))
            .catch(() => active && setData(null))
            .finally(() => active && setLoading(false));
        return () => {
            active = false;
        };
    }, [groupBy, branch, start, end, refreshKey, compare]);
    return { data, loading };
}

// ---------------------------------------------------------------- chart tooltip
function ChartTip({ active, payload, label, lang, unitByKey, labelFmt }) {
    if (!active || !payload || !payload.length) return null;
    return (
        <div
            dir={lang === "ar" ? "rtl" : "ltr"}
            className="rounded-lg border border-[color:var(--border-soft)] surface px-3 py-2 shadow-[var(--shadow-md)]"
        >
            <p className="text-xs font-semibold text-[color:var(--ink)] mb-1">
                {labelFmt ? labelFmt(label) : label}
            </p>
            {payload.map((p) => (
                <p key={p.dataKey} className="flex items-center gap-2 text-xs text-[color:var(--ink-muted)]">
                    <span className="h-2 w-2 rounded-full" style={{ background: p.color }} />
                    <span>{p.name}:</span>
                    <span className="tnum font-semibold text-[color:var(--ink)]">
                        {fmtNumber(p.value, lang, unitByKey[p.dataKey] === "number" ? 0 : 2)}
                    </span>
                </p>
            ))}
        </div>
    );
}

// ---------------------------------------------------------------- YoY compare helpers
// "MM-DD" -> localized day+month label (year-agnostic axis for the comparison).
function mdLabel(md, lang) {
    try {
        return new Date(`2000-${md}T00:00:00`).toLocaleDateString(
            lang === "ar" ? "ar-SA" : "en-US",
            { day: "numeric", month: "short" }
        );
    } catch {
        return md;
    }
}

function DeltaBadge({ cur, prev, lang }) {
    if (cur === null || cur === undefined || prev === null || prev === undefined || prev === 0) {
        return <span className="text-xs text-[color:var(--ink-muted)]">{"\u2014"}</span>;
    }
    const pct = ((cur - prev) / Math.abs(prev)) * 100;
    const up = pct >= 0;
    const Icon = up ? ArrowUpRight : ArrowDownRight;
    return (
        <span
            className={`inline-flex items-center gap-0.5 text-xs tnum font-semibold ${
                up ? "text-[color:var(--success)]" : "text-[color:var(--danger)]"
            }`}
        >
            <Icon className="h-3.5 w-3.5 shrink-0" />
            <bdi dir="ltr">{fmtNumber(Math.abs(pct), lang, 1)}%</bdi>
        </span>
    );
}

function CompareChart({ compare, metric }) {
    const { lang } = useI18n();
    const years = compare.years;
    const latest = years[years.length - 1];
    const mDef = compare.metrics.find((m) => m.key === metric) || {};
    const unitByKey = Object.fromEntries(years.map((y) => [String(y), mDef.unit]));
    const chartData = compare.rows.map((r) => {
        const o = { x: compare.align === "day" ? r.key : lang === "ar" ? r.label_ar : r.label_en };
        years.forEach((y) => {
            const v = r.values[String(y)];
            o[String(y)] = v ? v[metric] : null;
        });
        return o;
    });
    return (
        <div dir="ltr" data-testid={OPS.sales.explorerCompareChart}>
            <ResponsiveContainer width="100%" height={300}>
                <LineChart data={chartData} margin={{ top: 8, right: 12, left: 4, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={GRID_COLOR} vertical={false} />
                    <XAxis dataKey="x" tick={AXIS_TICK} minTickGap={28} tickLine={false} axisLine={{ stroke: GRID_COLOR }} />
                    <YAxis tickFormatter={fmtCompact} tick={AXIS_TICK} tickLine={false} axisLine={false} width={48} />
                    <Tooltip
                        content={(p) => (
                            <ChartTip
                                {...p}
                                lang={lang}
                                unitByKey={unitByKey}
                                labelFmt={(l) => (compare.align === "day" ? mdLabel(l, lang) : l)}
                            />
                        )}
                    />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    {years.map((y, i) => (
                        <Line
                            key={y}
                            type="monotone"
                            dataKey={String(y)}
                            name={String(y)}
                            stroke={CHART_COLORS[(years.length - 1 - i) % CHART_COLORS.length]}
                            strokeWidth={y === latest ? 2.5 : 1.8}
                            strokeDasharray={y === latest ? undefined : "6 3"}
                            dot={false}
                            activeDot={{ r: 4 }}
                            connectNulls
                        />
                    ))}
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
}

function CompareTable({ compare, metric, groupBy }) {
    const { lang, t } = useI18n();
    const years = compare.years;
    const latest = years[years.length - 1];
    const prevY = years[years.length - 2];
    const mDef = compare.metrics.find((m) => m.key === metric) || {};
    const rows = compare.rows;
    const scroll = rows.length > 12;
    const val = (r, y) => {
        const v = r.values[String(y)];
        return v ? v[metric] : null;
    };
    return (
        <div
            className={`overflow-x-auto rounded-lg border border-[color:var(--border-soft)] ${
                scroll ? "max-h-[460px] overflow-y-auto scrollbar-thin" : ""
            }`}
        >
            <Table data-testid={OPS.sales.explorerCompareTable}>
                <TableHeader className={scroll ? "sticky top-0 z-10" : ""}>
                    <TableRow className="bg-[color:rgba(224,222,217,0.92)] hover:bg-[color:rgba(224,222,217,0.92)]">
                        <TableHead className="text-start whitespace-nowrap">
                            {groupBy === "day" ? t("exp_gb_day") : t("exp_gb_month")}
                        </TableHead>
                        {years.map((y) => (
                            <TableHead key={y} className="text-end whitespace-nowrap">
                                <bdi dir="ltr">{y}</bdi>
                            </TableHead>
                        ))}
                        <TableHead className="text-end whitespace-nowrap">{t("exp_compare_change")}</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {rows.map((r) => (
                        <TableRow
                            key={r.key}
                            data-testid={OPS.sales.explorerCompareRow(r.key)}
                            className="hover:bg-[color:rgba(186,217,235,0.18)]"
                        >
                            <TableCell className="font-medium text-[color:var(--ink)] whitespace-nowrap">
                                {groupBy === "day" ? (
                                    <bdi dir="ltr">{mdLabel(r.key, lang)}</bdi>
                                ) : lang === "ar" ? (
                                    r.label_ar
                                ) : (
                                    r.label_en
                                )}
                            </TableCell>
                            {years.map((y) => (
                                <TableCell key={y} className="text-end tnum text-[color:var(--ink)]">
                                    {fmtByUnit(val(r, y), mDef.unit, lang)}
                                </TableCell>
                            ))}
                            <TableCell className="text-end whitespace-nowrap">
                                <DeltaBadge cur={val(r, latest)} prev={val(r, prevY)} lang={lang} />
                            </TableCell>
                        </TableRow>
                    ))}
                    <TableRow className="bg-[color:rgba(224,222,217,0.5)] hover:bg-[color:rgba(224,222,217,0.5)] font-bold">
                        <TableCell className="text-[color:var(--ink)]">{t("exp_compare_total")}</TableCell>
                        {years.map((y) => (
                            <TableCell key={y} className="text-end tnum text-[color:var(--ink)]">
                                {fmtByUnit(compare.totals?.[String(y)]?.[metric], mDef.unit, lang)}
                            </TableCell>
                        ))}
                        <TableCell className="text-end whitespace-nowrap">
                            <DeltaBadge
                                cur={compare.totals?.[String(latest)]?.[metric]}
                                prev={compare.totals?.[String(prevY)]?.[metric]}
                                lang={lang}
                            />
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        </div>
    );
}

// ---------------------------------------------------------------- summary strip
// Roomy, responsive grid (max 3 across) so long currency figures never overflow
// their card on desktop, tablet, or phone. Exact value is kept; a title tooltip
// exposes the full figure and truncation is a last-resort guard.
const SUMMARY_GRID = "grid grid-cols-2 lg:grid-cols-3 gap-3";

function SummaryStrip({ summary, prev, loading }) {
    const { t, lang } = useI18n();
    if (loading) {
        return (
            <div className={SUMMARY_GRID}>
                {Array.from({ length: 6 }).map((_, i) => (
                    <Skeleton key={i} className="h-[92px] rounded-2xl" />
                ))}
            </div>
        );
    }
    const cards = SUMMARY_DEFS.filter(
        (d) => summary && summary[d.key] !== null && summary[d.key] !== undefined
    );
    if (!cards.length) return null;
    return (
        <div data-testid={OPS.sales.explorerSummary} className={SUMMARY_GRID}>
            {cards.map((c) => {
                const valueStr = fmtByUnit(summary[c.key], c.unit, lang);
                const isCurrency = c.unit === "currency";
                const fullTitle = isCurrency ? `${valueStr} ${currencySuffix(lang)}` : valueStr;
                const prevVal = prev?.summary ? prev.summary[c.key] : null;
                const showDelta = prevVal !== null && prevVal !== undefined && summary[c.key] !== null;
                return (
                    <div
                        key={c.key}
                        data-testid={OPS.sales.explorerSummaryCard(c.key)}
                        className="flex min-h-[92px] min-w-0 flex-col justify-between rounded-2xl surface-2 border border-[color:var(--border-soft)] p-4 transition-shadow duration-200 hover:shadow-[var(--shadow-sm)]"
                    >
                        <p className="truncate text-xs font-medium text-[color:var(--ink-muted)]">
                            {t(c.labelKey)}
                        </p>
                        <div className="mt-1.5 flex min-w-0 items-baseline gap-1" title={fullTitle}>
                            <span className="truncate text-sm font-extrabold tracking-tight tnum text-[color:var(--ink)] sm:text-base md:text-lg lg:text-xl">
                                {valueStr}
                            </span>
                            {isCurrency ? (
                                <span className="shrink-0 text-[11px] font-medium text-[color:var(--ink-muted)]">
                                    {currencySuffix(lang)}
                                </span>
                            ) : null}
                        </div>
                        {showDelta ? (
                            <div
                                data-testid={OPS.sales.explorerSummaryDelta(c.key)}
                                className="mt-1 flex min-w-0 flex-wrap items-center gap-1"
                            >
                                <DeltaBadge cur={summary[c.key]} prev={prevVal} lang={lang} />
                                <span className="truncate text-[11px] text-[color:var(--ink-muted)]">
                                    {t("exp_vs")} <bdi dir="ltr">{prev.year}</bdi>
                                </span>
                            </div>
                        ) : null}
                    </div>
                );
            })}
        </div>
    );
}

// ---------------------------------------------------------------- chart (adapts per group_by)
function ExplorerChart({ data, groupBy, channelMode }) {
    const { lang } = useI18n();
    const columns = data?.table?.columns || [];
    const rows = data?.table?.rows || [];
    const GroupIcon = GROUP_ICON[groupBy] || Compass;

    if (!rows.length) {
        return <EmptyState compact icon={GroupIcon} title={undefined} dataset="sales_daily_network" />;
    }

    const TOOLTIP_STYLE = {
        borderRadius: 12,
        border: "1px solid var(--border-soft)",
        fontSize: 12,
        background: "var(--bg-surface)",
    };

    // --- day + channel breakdown → stacked bars (walk-in / call-center / delivery)
    if (groupBy === "day" && channelMode && data?.channel_cols) {
        const chartData = rows.map((r) => ({
            x: r.id,
            walk_in: r.values.walk_in ?? 0,
            call_center: r.values.call_center ?? 0,
            delivery: r.values.delivery ?? 0,
        }));
        const chLabel = {
            walk_in: lang === "ar" ? "مباشر بالفرع" : "Walk-in",
            call_center: lang === "ar" ? "الكول سنتر" : "Call center",
            delivery: lang === "ar" ? "تطبيقات التوصيل" : "Delivery apps",
        };
        return (
            <div dir="ltr" data-testid={OPS.sales.explorerChart}>
                <ResponsiveContainer width="100%" height={300}>
                    <ComposedChart data={chartData} margin={{ top: 8, right: 8, bottom: 4, left: 4 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={GRID_COLOR} vertical={false} />
                        <XAxis dataKey="x" tickFormatter={shortDate} tick={AXIS_TICK} minTickGap={24} tickLine={false} axisLine={{ stroke: GRID_COLOR }} />
                        <YAxis tickFormatter={fmtCompact} tick={AXIS_TICK} width={54} tickLine={false} axisLine={false} />
                        <Tooltip
                            formatter={(value, name) => [`${fmtNumber(value, lang, 0)} ${currencySuffix(lang)}`, chLabel[name] || name]}
                            labelFormatter={(l) => localeDate(l, lang)}
                            contentStyle={TOOLTIP_STYLE}
                        />
                        <Legend formatter={(v) => chLabel[v] || v} wrapperStyle={{ fontSize: 12 }} />
                        <Bar dataKey="walk_in" stackId="a" fill={CH_COLORS.walk_in} />
                        <Bar dataKey="call_center" stackId="a" fill={CH_COLORS.call_center} />
                        <Bar dataKey="delivery" stackId="a" fill={CH_COLORS.delivery} radius={[3, 3, 0, 0]} />
                    </ComposedChart>
                </ResponsiveContainer>
            </div>
        );
    }

    // --- channel → donut
    if (groupBy === "channel") {
        const showMoney = columns.some((c) => c.key === "value");
        const pieData = rows.map((r) => ({
            name: lang === "ar" ? r.label_ar : r.label_en,
            key: r.id,
            value: r.values.value ?? r.values.count ?? r.values.share ?? 0,
            share: r.values.share,
        }));
        return (
            <div dir="ltr" data-testid={OPS.sales.explorerChart}>
                <ResponsiveContainer width="100%" height={260}>
                    <PieChart>
                        <Pie
                            data={pieData}
                            dataKey="value"
                            nameKey="name"
                            innerRadius={64}
                            outerRadius={100}
                            paddingAngle={2}
                            stroke="var(--bg-surface)"
                            strokeWidth={2}
                        >
                            {pieData.map((entry, i) => (
                                <Cell key={entry.key} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                            ))}
                        </Pie>
                        <Tooltip
                            content={({ active, payload }) => {
                                if (!active || !payload?.length) return null;
                                const d = payload[0].payload;
                                return (
                                    <div
                                        dir={lang === "ar" ? "rtl" : "ltr"}
                                        className="rounded-lg border border-[color:var(--border-soft)] surface px-3 py-2 shadow-[var(--shadow-md)] text-xs"
                                    >
                                        <p className="font-semibold text-[color:var(--ink)]">{d.name}</p>
                                        {showMoney ? (
                                            <p className="text-[color:var(--ink-muted)] tnum">
                                                {fmtNumber(d.value, lang, 2)} {currencySuffix(lang)}
                                            </p>
                                        ) : null}
                                        {d.share != null ? (
                                            <p className="text-[color:var(--ink-muted)] tnum">
                                                {fmtNumber(d.share, lang, 1)}%
                                            </p>
                                        ) : null}
                                    </div>
                                );
                            }}
                        />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                    </PieChart>
                </ResponsiveContainer>
            </div>
        );
    }

    // --- branch / item → horizontal bar (ranking)
    if (groupBy === "branch" || groupBy === "item") {
        const moneyCol =
            columns.find((c) => c.key === "revenue") ||
            columns.find((c) => c.unit === "currency") ||
            columns.find((c) => c.unit === "number");
        if (!moneyCol) return null;
        const top = (groupBy === "item" ? rows.slice(0, 10) : rows).map((r) => ({
            name: lang === "ar" ? r.label_ar : r.label_en,
            v: r.values[moneyCol.key],
        }));
        // Largest on top: recharts renders category axis bottom-up, so reverse.
        const chartData = [...top].reverse();
        const height = Math.max(240, chartData.length * 34 + 40);
        const colLabel = lang === "ar" ? moneyCol.name_ar : moneyCol.name_en;
        return (
            <div dir="ltr" data-testid={OPS.sales.explorerChart}>
                <ResponsiveContainer width="100%" height={height}>
                    <ComposedChart layout="vertical" data={chartData} margin={{ top: 4, right: 16, bottom: 4, left: 4 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={GRID_COLOR} horizontal={false} />
                        <XAxis type="number" tickFormatter={fmtCompact} tick={AXIS_TICK} tickLine={false} axisLine={{ stroke: GRID_COLOR }} />
                        <YAxis type="category" dataKey="name" width={118} tick={{ fontSize: 11, fill: "var(--ink)" }} tickLine={false} axisLine={false} />
                        <Tooltip
                            cursor={{ fill: "rgba(186,217,235,0.18)" }}
                            formatter={(value) => [`${fmtNumber(value, lang, moneyCol.unit === "number" ? 0 : 2)}${moneyCol.unit === "currency" ? " " + currencySuffix(lang) : ""}`, colLabel]}
                            contentStyle={TOOLTIP_STYLE}
                        />
                        <Bar dataKey="v" name={colLabel} fill={CHART_COLORS[0]} radius={[0, 4, 4, 0]} maxBarSize={26} />
                    </ComposedChart>
                </ResponsiveContainer>
            </div>
        );
    }

    // --- day (normal) / month → line / composed
    const money = columns.filter((c) => c.unit === "currency" && !CHANNEL_KEYS.includes(c.key));
    const count = columns.filter((c) => c.unit === "number");
    const unitByKey = Object.fromEntries(columns.map((c) => [c.key, c.unit]));
    const nameByKey = Object.fromEntries(columns.map((c) => [c.key, lang === "ar" ? c.name_ar : c.name_en]));
    const chartData = rows.map((r) => ({
        x: groupBy === "day" ? r.id : lang === "ar" ? r.label_ar : r.label_en,
        ...r.values,
    }));

    if (groupBy === "day") {
        return (
            <div dir="ltr" data-testid={OPS.sales.explorerChart}>
                <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={chartData} margin={{ top: 8, right: 12, left: 4, bottom: 4 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={GRID_COLOR} vertical={false} />
                        <XAxis dataKey="x" tickFormatter={shortDate} tick={AXIS_TICK} minTickGap={28} tickLine={false} axisLine={{ stroke: GRID_COLOR }} />
                        <YAxis yAxisId="money" tickFormatter={fmtCompact} tick={AXIS_TICK} tickLine={false} axisLine={false} width={48} />
                        {count.length ? (
                            <YAxis yAxisId="count" orientation="right" tickFormatter={fmtCompact} tick={AXIS_TICK} tickLine={false} axisLine={false} width={42} />
                        ) : null}
                        <Tooltip content={(p) => <ChartTip {...p} lang={lang} unitByKey={unitByKey} labelFmt={(l) => localeDate(l, lang)} />} />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                        {money.map((s, i) => (
                            <Line key={s.key} yAxisId="money" type="monotone" dataKey={s.key} name={nameByKey[s.key]} stroke={CHART_COLORS[i % CHART_COLORS.length]} strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
                        ))}
                        {count.map((s, i) => (
                            <Line key={s.key} yAxisId="count" type="monotone" dataKey={s.key} name={nameByKey[s.key]} stroke={CHART_COLORS[(money.length + i) % CHART_COLORS.length]} strokeWidth={1.5} strokeDasharray="4 2" dot={false} />
                        ))}
                    </LineChart>
                </ResponsiveContainer>
            </div>
        );
    }

    // month
    return (
        <div dir="ltr" data-testid={OPS.sales.explorerChart}>
            <ResponsiveContainer width="100%" height={300}>
                <ComposedChart data={chartData} margin={{ top: 8, right: 12, left: 4, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={GRID_COLOR} vertical={false} />
                    <XAxis dataKey="x" tick={AXIS_TICK} tickLine={false} axisLine={{ stroke: GRID_COLOR }} />
                    <YAxis yAxisId="money" tickFormatter={fmtCompact} tick={AXIS_TICK} tickLine={false} axisLine={false} width={48} />
                    {count.length ? (
                        <YAxis yAxisId="count" orientation="right" tickFormatter={fmtCompact} tick={AXIS_TICK} tickLine={false} axisLine={false} width={42} />
                    ) : null}
                    <Tooltip cursor={{ fill: "rgba(186,217,235,0.18)" }} content={(p) => <ChartTip {...p} lang={lang} unitByKey={unitByKey} />} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    {money.map((s, i) => (
                        <Bar key={s.key} yAxisId="money" dataKey={s.key} name={nameByKey[s.key]} fill={CHART_COLORS[i % CHART_COLORS.length]} radius={[4, 4, 0, 0]} maxBarSize={48} />
                    ))}
                    {count.map((s) => (
                        <Line key={s.key} yAxisId="count" type="monotone" dataKey={s.key} name={nameByKey[s.key]} stroke={CHART_COLORS[2]} strokeWidth={2} dot={{ r: 3 }} />
                    ))}
                </ComposedChart>
            </ResponsiveContainer>
        </div>
    );
}

// ---------------------------------------------------------------- main table
function ExplorerTable({ data, groupBy, channelMode }) {
    const { lang, t } = useI18n();
    const table = data?.table || {};
    const rows = table.rows || [];
    let columns = table.columns || [];

    // Day view: the channel columns exist only when channel_cols is true.
    // The channel toggle switches which set of columns is shown.
    if (groupBy === "day" && data?.channel_cols) {
        columns = channelMode
            ? columns.filter((c) => CHANNEL_KEYS.includes(c.key))
            : columns.filter((c) => !CHANNEL_KEYS.includes(c.key));
    }

    const idLabel = lang === "ar" ? table.id_label_ar : table.id_label_en;
    const GroupIcon = GROUP_ICON[groupBy] || Compass;

    if (!rows.length || !columns.length) {
        return <EmptyState icon={GroupIcon} title={t("no_rows_range")} dataset="sales_daily_network" />;
    }

    const scroll = rows.length > 12;
    return (
        <div
            className={`overflow-x-auto rounded-lg border border-[color:var(--border-soft)] ${
                scroll ? "max-h-[460px] overflow-y-auto scrollbar-thin" : ""
            }`}
        >
            <Table data-testid={OPS.sales.explorerTable}>
                <TableHeader className={scroll ? "sticky top-0 z-10" : ""}>
                    <TableRow className="bg-[color:rgba(224,222,217,0.92)] hover:bg-[color:rgba(224,222,217,0.92)]">
                        <TableHead className="text-start whitespace-nowrap">{idLabel}</TableHead>
                        {columns.map((col) => (
                            <TableHead key={col.key} className="text-end whitespace-nowrap">
                                {lang === "ar" ? col.name_ar : col.name_en}
                            </TableHead>
                        ))}
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {rows.map((r) => {
                        const isDate = groupBy === "day";
                        const label = lang === "ar" ? r.label_ar : r.label_en;
                        return (
                            <TableRow
                                key={String(r.id)}
                                data-testid={OPS.sales.explorerRow(r.id)}
                                className="hover:bg-[color:rgba(186,217,235,0.18)]"
                            >
                                <TableCell className="font-medium text-[color:var(--ink)] whitespace-nowrap">
                                    {isDate ? (
                                        <bdi dir="ltr">{localeDate(r.id, lang)}</bdi>
                                    ) : (
                                        <span className="block truncate max-w-[280px]">{label}</span>
                                    )}
                                    {r.category ? (
                                        <span className="block text-[11px] text-[color:var(--ink-muted)] truncate max-w-[280px]">
                                            {r.category}
                                        </span>
                                    ) : null}
                                    {r.through ? (
                                        <span className="block text-[11px] text-[color:var(--ink-muted)]">
                                            {t("sc_mtd_through")} <bdi dir="ltr">{r.through}</bdi>
                                        </span>
                                    ) : null}
                                </TableCell>
                                {columns.map((col) => (
                                    <TableCell key={col.key} className="text-end tnum text-[color:var(--ink)]">
                                        {fmtByUnit(r.values[col.key], col.unit, lang)}
                                    </TableCell>
                                ))}
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </div>
    );
}

// ---------------------------------------------------------------- channel-mix segmented bar (channel view only)
function ChannelMixBar({ mix }) {
    const { lang } = useI18n();
    const channels = mix?.channels || [];
    if (!channels.length || !mix?.data_available) return null;
    return (
        <div className="space-y-3">
            <div
                data-testid={OPS.sales.explorerMixBar}
                dir={lang === "ar" ? "rtl" : "ltr"}
                className="flex h-6 w-full overflow-hidden rounded-full border border-[color:var(--border-soft)]"
            >
                {channels.map((c) => (
                    <div
                        key={c.key}
                        className="h-full transition-[width] duration-300"
                        style={{ width: `${c.share || 0}%`, background: c.color }}
                        title={`${lang === "ar" ? c.name_ar : c.name_en} — ${fmtNumber(c.share, lang, 1)}%`}
                    />
                ))}
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1.5">
                {channels.map((c) => (
                    <span key={c.key} className="flex items-center gap-2 text-xs text-[color:var(--ink-muted)]">
                        <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: c.color }} />
                        <span className="text-[color:var(--ink)] font-medium">
                            {lang === "ar" ? c.name_ar : c.name_en}
                        </span>
                        <span className="tnum text-[color:var(--brand-brown)] font-semibold">
                            {fmtNumber(c.share, lang, 1)}%
                        </span>
                    </span>
                ))}
            </div>
        </div>
    );
}

// ---------------------------------------------------------------- root component
export default function SalesExplorer({ branch, start, end, groups, refreshKey }) {
    const { t, lang } = useI18n();
    const options = GROUP_OPTIONS.filter((o) => !groups || groups.includes(o.key));
    const initialGroup = groups && groups.length ? (groups.includes("month") ? "month" : groups[0]) : "month";
    const [groupBy, setGroupBy] = useState(initialGroup);
    const [channelMode, setChannelMode] = useState(false);
    const [chartOpen, setChartOpen] = useState(true);
    const [compareMode, setCompareMode] = useState(false);
    const [compareMetric, setCompareMetric] = useState(null);
    const showCompareToggle = groupBy === "day" || groupBy === "month";
    const wantCompare = compareMode && showCompareToggle;
    const { data, loading } = useExplore(groupBy, branch, start, end, refreshKey, wantCompare);

    const cmp = wantCompare ? data?.compare : null;
    const cmpActive = !!cmp?.available;

    useEffect(() => {
        if (cmp?.available && cmp.metrics?.length) {
            setCompareMetric((m) =>
                m && cmp.metrics.some((x) => x.key === m) ? m : cmp.metrics[0].key
            );
        }
    }, [cmp]);

    const showChannelToggle = groupBy === "day" && !!data?.channel_cols && !compareMode;
    const sourceNote = data?.source_note_ar
        ? lang === "ar"
            ? data.source_note_ar
            : data.source_note_en
        : null;
    const infoNote = data?.note_ar ? (lang === "ar" ? data.note_ar : data.note_en) : null;
    const hasData = data?.data_available;

    return (
        <DataScope month={monthOf(end)}>
        <Card
            data-testid={OPS.sales.explorer}
            className="surface border-[color:var(--border-soft)] rounded-xl shadow-[var(--shadow-sm)]"
        >
            <CardHeader className="space-y-4 pb-4">
                <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                        <CardTitle className="flex items-center gap-2 text-base font-bold text-[color:var(--ink)]">
                            <Compass className="h-[18px] w-[18px] text-[color:var(--brand-brown)]" />
                            {t("exp_title")}
                        </CardTitle>
                        <p className="mt-1 text-xs text-[color:var(--ink-muted)]">{t("exp_subtitle")}</p>
                    </div>
                    {sourceNote ? (
                        <Badge
                            variant="secondary"
                            className="bg-[color:rgba(186,217,235,0.5)] text-[color:var(--brand-brown)] border-0 font-medium"
                        >
                            {sourceNote}
                        </Badge>
                    ) : null}
                </div>

                <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-medium text-[color:var(--ink-muted)]">
                            {t("exp_group_by")}
                        </span>
                        <ToggleGroup
                            type="single"
                            value={groupBy}
                            onValueChange={(v) => v && setGroupBy(v)}
                            className="flex-wrap justify-start gap-1 rounded-lg border border-[color:var(--border-soft)] surface-2 p-1"
                        >
                            {options.map((o) => {
                                const Icon = o.icon;
                                return (
                                    <ToggleGroupItem
                                        key={o.key}
                                        value={o.key}
                                        data-testid={OPS.sales.explorerGroupBy(o.key)}
                                        aria-label={t(o.labelKey)}
                                        className="gap-1.5 rounded-md px-3 py-1.5 text-sm font-semibold text-[color:var(--ink-muted)] transition-[color,background-color] duration-200 hover:text-[color:var(--ink)] data-[state=on]:bg-[color:var(--brand-orange)] data-[state=on]:text-white data-[state=on]:shadow-[var(--shadow-sm)]"
                                    >
                                        <Icon className="h-4 w-4" />
                                        {t(o.labelKey)}
                                    </ToggleGroupItem>
                                );
                            })}
                        </ToggleGroup>
                    </div>

                    <div className="flex items-center gap-4 flex-wrap">
                        {showCompareToggle ? (
                            <div className="flex items-center gap-2">
                                <Switch
                                    id="explorer-compare-toggle"
                                    data-testid={OPS.sales.explorerCompareToggle}
                                    checked={compareMode}
                                    onCheckedChange={setCompareMode}
                                    className="data-[state=checked]:bg-[color:var(--brand-brown)]"
                                />
                                <Label
                                    htmlFor="explorer-compare-toggle"
                                    className="text-sm font-medium text-[color:var(--ink)] cursor-pointer"
                                >
                                    {t("exp_compare_yoy")}
                                </Label>
                            </div>
                        ) : null}
                        {showChannelToggle ? (
                            <div className="flex items-center gap-2">
                                <Switch
                                    id="explorer-channel-toggle"
                                    data-testid={OPS.sales.explorerChannelToggle}
                                    checked={channelMode}
                                    onCheckedChange={setChannelMode}
                                    className="data-[state=checked]:bg-[color:var(--brand-orange)]"
                                />
                                <Label
                                    htmlFor="explorer-channel-toggle"
                                    className="text-sm font-medium text-[color:var(--ink)] cursor-pointer"
                                >
                                    {t("exp_channel_breakdown")}
                                </Label>
                            </div>
                        ) : null}
                    </div>
                </div>

                {infoNote ? (
                    <p className="text-[11px] leading-snug text-[color:var(--ink-muted)]">{infoNote}</p>
                ) : null}
            </CardHeader>

            <CardContent className="space-y-5">
                <SummaryStrip summary={data?.summary} prev={cmpActive ? cmp.prev : null} loading={loading} />

                {!loading && cmp && !cmp.available ? (
                    <div
                        data-testid={OPS.sales.explorerCompareNote}
                        className="rounded-lg border border-[color:var(--border-soft)] surface-2 px-4 py-3 text-xs leading-relaxed text-[color:var(--ink-muted)]"
                    >
                        {lang === "ar" ? cmp.note_ar : cmp.note_en}
                    </div>
                ) : null}

                {loading ? (
                    <>
                        <Skeleton className="h-[300px] rounded-xl" />
                        <Skeleton className="h-[220px] rounded-xl" />
                    </>
                ) : !hasData ? (
                    (groupBy === "item" || groupBy === "channel")
                        && (groupBy === "item" ? data?.item_coverage?.to : data?.channel_coverage?.to) ? (
                        <EmptyState
                            icon={groupBy === "item" ? Package : PieIcon}
                            testId={OPS.sales.itemGapEmpty}
                            title={t(groupBy === "item" ? "exp_item_gap_title" : "exp_channel_gap_title")}
                            hint={t("exp_breakdown_gap_hint")
                                .replace("{from}", lang === "ar"
                                    ? (groupBy === "item" ? data.item_coverage : data.channel_coverage).from_label_ar
                                    : (groupBy === "item" ? data.item_coverage : data.channel_coverage).from_label_en)
                                .replace("{to}", lang === "ar"
                                    ? (groupBy === "item" ? data.item_coverage : data.channel_coverage).to_label_ar
                                    : (groupBy === "item" ? data.item_coverage : data.channel_coverage).to_label_en)}
                        />
                    ) : (
                        <EmptyState icon={GROUP_ICON[groupBy] || Compass} title={t("no_rows_range")}
                            dataset={groupBy === "item" ? "sales_month_items" : "sales_daily_network"} />
                    )
                ) : (
                    <>
                        {cmpActive && compareMetric ? (
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-xs font-medium text-[color:var(--ink-muted)]">
                                    {t("exp_compare_metric")}
                                </span>
                                <ToggleGroup
                                    type="single"
                                    value={compareMetric}
                                    onValueChange={(v) => v && setCompareMetric(v)}
                                    className="flex-wrap justify-start gap-1 rounded-lg border border-[color:var(--border-soft)] surface-2 p-1"
                                >
                                    {cmp.metrics.map((m) => (
                                        <ToggleGroupItem
                                            key={m.key}
                                            value={m.key}
                                            data-testid={OPS.sales.explorerCompareMetric(m.key)}
                                            className="rounded-md px-3 py-1 text-xs font-semibold text-[color:var(--ink-muted)] transition-[color,background-color] duration-200 hover:text-[color:var(--ink)] data-[state=on]:bg-[color:var(--brand-brown)] data-[state=on]:text-white"
                                        >
                                            {lang === "ar" ? m.name_ar : m.name_en}
                                        </ToggleGroupItem>
                                    ))}
                                </ToggleGroup>
                            </div>
                        ) : null}

                        <div className="space-y-3">
                            <div className="flex items-center justify-between">
                                <span className="text-sm font-semibold text-[color:var(--ink)]">
                                    {t("exp_chart")}
                                </span>
                                <Button
                                    data-testid={OPS.sales.explorerChartToggle}
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setChartOpen((v) => !v)}
                                    aria-expanded={chartOpen}
                                    className="gap-1.5 text-[color:var(--ink-muted)] hover:text-[color:var(--ink)]"
                                >
                                    {chartOpen ? t("exp_chart_hide") : t("exp_chart_show")}
                                    <ChevronDown
                                        className={`h-4 w-4 transition-transform duration-200 ${
                                            chartOpen ? "rotate-180" : ""
                                        }`}
                                    />
                                </Button>
                            </div>
                            {chartOpen ? (
                                cmpActive && compareMetric ? (
                                    <CompareChart compare={cmp} metric={compareMetric} />
                                ) : (
                                    <ExplorerChart data={data} groupBy={groupBy} channelMode={channelMode} />
                                )
                            ) : null}
                        </div>

                        {cmpActive && compareMetric ? (
                            <CompareTable compare={cmp} metric={compareMetric} groupBy={groupBy} />
                        ) : (
                            <ExplorerTable data={data} groupBy={groupBy} channelMode={channelMode} />
                        )}

                        {groupBy === "channel" ? <ChannelMixBar mix={data?.channel_mix} /> : null}
                    </>
                )}
            </CardContent>
        </Card>
        </DataScope>
    );
}
